<template>
	<client-only>
		<carousel
			v-if="showCarousel"
			ref="carouselEl"
			:settings="settings"
			class="py-5"
			:class="`carousel-dj carousel-testimonials carousel-buttons-${buttonType}`"
		>
			<slide v-for="(item, index) in getContent().concat(new Array(helperSlides))" :key="index">
				<div v-if="index > getContent().length - 1"></div>

				<div v-else class="w-full px-14 sm:px-0 flex justify-center">
					<DjCardTestimonial
						:main-text="item.mainText"
						:full-name="item.fullName"
						:position="item.position"
						:company-name="item.companyName"
						:photo-url="item.photoUrl"
					/>
				</div>
			</slide>

			<template v-if="showCarouselAddons()" #addons="{ currentSlide, slidesCount }">
				<!-- Custom navigation -->
				<div class="carousel__navigation-buttons mx-auto">
					<button
						class="carousel__prev transition-colors"
						:class="{ 'hide-prev': Math.ceil(currentSlide) < 1 }"
						@click="slidePrev(currentSlide, containerSlideNumber)"
					></button>
					<button
						class="carousel__next transition-colors"
						:class="{
							'hide-next': Math.ceil(currentSlide) >= slidesCount - containerSlideNumber - helperSlides,
						}"
						@click="
							slideTo(
								currentSlide + containerSlideNumber,
								slidesCount - helperSlides - containerSlideNumber
							)
						"
					></button>
				</div>

				<!-- Custom pagination -->
				<div class="hidden sm:block carousel__custom-pagination mx-auto">
					<button
						v-for="(paginationEl, index) in Math.ceil((slidesCount - helperSlides) / containerSlideNumber)"
						:key="index"
						class="h-4 w-4 m-2 bg-dj-gray-light rounded-full"
						:class="{
							'dot--active': activePagination(
								index,
								currentSlide,
								slidesCount,
								containerSlideNumber,
								helperSlides
							),
						}"
						@click="
							slideTo(index * containerSlideNumber, slidesCount - helperSlides - containerSlideNumber)
						"
					></button>
				</div>
			</template>
		</carousel>

		<template #fallback>
			<div class="block w-[1000vw] sm:ml-10 mb-16 pl-5 sm:pl-0 pt-2 pb-2">
				<div
					v-for="(item, index) in ssrStaticTestimonials"
					:key="index"
					class="inline-block float-left w-[280px] sm:w-[400px] mr-6 sm:mr-10"
				>
					<DjCardTestimonial
						:main-text="item.mainText"
						:full-name="item.fullName"
						:position="item.position"
						:company-name="item.companyName"
						:photo-url="item.photoUrl"
					/>
				</div>
			</div>
		</template>
	</client-only>
</template>

<script lang="ts">
import { ref, defineComponent, computed, onMounted } from "vue";
import { Carousel, Slide } from "vue3-carousel";
import { getDjContainerWidth } from "../../composables/carouselHelpers";
import useCarouselTestimonials from "../../composables/useCarouselTestimonials";

export default defineComponent({
	name: "CarouselTestimonials",
	components: {
		Carousel,
		Slide,
	},
	props: {
		slideWidth: {
			type: Number,
			default: 280,
		},
		buttonType: {
			type: String,
			default: "white",
			validator: (value) => {
				return ["white", "gray"].includes(value);
			},
		},
	},
	emits: ["loaded"],
	setup(props, { emit }) {
		const { testimonials } = useCarouselTestimonials();

		function getContent() {
			emit("loaded");
			return testimonials.value;
		}

		function isMobile() {
			if (typeof document === "undefined") return false;
			const body = document.querySelector("body");
			return body ? body.offsetWidth < 741 : false;
		}

		const carouselEl = ref(null);
		let showCarousel = ref(false);
		let bodyWidth = typeof document !== "undefined" ? document.querySelector("body")?.offsetWidth || 0 : 0;
		let slideMargin = ref(20);
		let helperSlides = ref(0);
		let containerSlideNumber = ref(0);

		function getItemsNumber() {
			if (isMobile()) {
				return 1;
			}

			return bodyWidth / (getValidSlideWidth() + slideMargin.value);
		}

		function showCarouselAddons() {
			const containerWidth = getDjContainerWidth();
			return (
				(bodyWidth - (bodyWidth - containerWidth) / 2) / (getValidSlideWidth() + slideMargin.value) <
				getContent().length
			);
		}

		function slideTo(i, maxIndex) {
			carouselEl.value?.slideTo(i <= maxIndex ? i : maxIndex);
		}

		function slidePrev(currentSlide, containerSlideNumber) {
			let goToIndex =
				currentSlide -
				(currentSlide % containerSlideNumber == 0 ? containerSlideNumber : currentSlide % containerSlideNumber);
			carouselEl.value?.slideTo(goToIndex);
		}

		function activePagination(index, currentSlide, slidesCount, containerSlideNumber, helperSlides) {
			const lastPagination = index == Math.ceil((slidesCount - helperSlides) / containerSlideNumber) - 1;
			if (lastPagination && Math.ceil(currentSlide) >= slidesCount - containerSlideNumber - helperSlides) {
				return true;
			} else if (
				Math.floor(currentSlide / containerSlideNumber) == index &&
				Math.ceil(currentSlide) < slidesCount - containerSlideNumber - helperSlides
			) {
				return true;
			}

			return false;
		}

		let settings = {
			itemsToShow: 1,
			snapAlign: "start",
			mouseDrag: true,
			touchDrag: true,
			transition: 800,
		};

		if (process.client) {
			calculateCarouselParams();
			showCarousel.value = true;
		}

		function getValidSlideWidth() {
			const contWidth = getDjContainerWidth();
			let slideWidth = props.slideWidth;

			slideWidth = contWidth > 600 ? 580 : props.slideWidth;
			slideWidth = contWidth > 980 ? 452 : slideWidth;
			slideWidth = contWidth > 1300 ? 408 : slideWidth;

			return slideWidth;
		}

		function calculateCarouselParams() {
			let containerWidth = getDjContainerWidth();
			containerSlideNumber.value = Math.floor(containerWidth / getValidSlideWidth());
			bodyWidth = typeof document !== "undefined" ? document.querySelector("body")?.offsetWidth || 0 : 0;
			slideMargin.value = isMobile() ? 20 : 40;

			settings.itemsToShow = getItemsNumber();

			settings.snapAlign = isMobile() ? "center" : "start";

			helperSlides.value =
				Math.ceil((bodyWidth - containerWidth) / 2 / (getValidSlideWidth() + slideMargin.value)) +
				(bodyWidth > 1790 ? 1 : 0);
			if (bodyWidth < 741) {
				helperSlides.value = 0;
			}
		}

		function isResized() {
			showCarousel.value = false;
			setTimeout(() => {
				calculateCarouselParams();
				showCarousel.value = true;
			}, 500);
		}

		let doResize = null;
		function handleResize() {
			clearTimeout(doResize ?? undefined);
			const currentBodyWidth =
				typeof document !== "undefined" ? document.querySelector("body")?.offsetWidth || 0 : 0;
			bodyWidth != currentBodyWidth ? (doResize = setTimeout(isResized, 500)) : null;
		}

		onMounted(async () => {
			window.addEventListener("resize", handleResize);
		});

		const ssrStaticTestimonials = computed(() => testimonials.value.slice(0, 5));

		return {
			carouselEl,
			settings,
			slideMargin,
			showCarousel,
			helperSlides,
			getContent,
			isMobile,
			getItemsNumber,
			showCarouselAddons,
			containerSlideNumber,
			slidePrev,
			slideTo,
			activePagination,
			ssrStaticTestimonials,
		};
	},
});
</script>
